/**
 * OrbitPrint — dashboard: every Location with its printers and live status.
 * Approved prototype look (2026-09-16). Live over the socket (slice 5): the
 * server says "locations changed" and the page re-fetches; the 15 s poll is
 * the fallback and relaxes to 60 s while the line is open. Controls that
 * cannot work yet are faded with copy that says why — never hidden, never
 * live-but-dead (owner rule).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useOrbitAuth, useOrbitPath, orbitApi, errText } from '../OrbitAuthContext';
import { useOrbitLive, useLiveRefresh, usePollMs } from '../live';
import { OrbitWordmark, OrbitButton, OrbitInput, PrinterIcon, Lamp } from '../components/OrbitShell';
import LocationCard from '../components/LocationCard';
import PrintFileDialog from '../components/PrintFileDialog';
import JobsPanel, { UsageBadge } from '../components/JobsPanel';
import PlanBanner from '../components/PlanBanner';
import '../orbit.css';

const POLL_LIVE_MS = 60000;
const POLL_FALLBACK_MS = 15000;

/** The line to the server, stated honestly: live, connecting, or falling back to polling. */
export function LiveLamp() {
  const { state } = useOrbitLive();
  if (state === 'live') return <Lamp state="ready" testid="orbit-live-lamp" title="Updates arrive the moment they happen">Live</Lamp>;
  if (state === 'connecting') return <Lamp state="busy" testid="orbit-live-lamp" title="Opening the live connection">Connecting</Lamp>;
  return <Lamp state="offline" testid="orbit-live-lamp" title="No live connection — this page checks for changes every 15 seconds">Checking every 15 s</Lamp>;
}

function TopBar() {
  const { user, org, orgs, logout, switchOrg } = useOrbitAuth();
  const p = useOrbitPath();
  const manager = org?.role === 'owner' || org?.role === 'admin';
  const [busy, setBusy] = useState(false);
  const onSwitch = async (e) => {
    const id = e.target.value;
    if (!id || id === org?.id) return;
    setBusy(true);
    try { await switchOrg(id); } catch (err) { toast.error(errText(err)); } finally { setBusy(false); }
  };
  return (
    <header className="orbit-topbar">
      <div className="mx-auto max-w-6xl px-5 sm:px-8 h-16 flex items-center justify-between gap-4">
        <OrbitWordmark small />
        <div className="flex items-center gap-3 min-w-0">
          {orgs.length > 1 ? (
            <select className="orbit-input !text-sm !w-auto max-w-[220px]" value={org?.id || ''} onChange={onSwitch} disabled={busy}
              aria-label="Organization" data-testid="orbit-org-switch">
              {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          ) : (
            <span className="hidden sm:inline text-sm truncate orbit-ink2" data-testid="orbit-org-name">{org?.name}</span>
          )}
          {manager && <Link to={p('/members')} className="orbit-link is-muted text-sm" data-testid="orbit-members-link">Members</Link>}
          <span className="hidden sm:inline-flex"><LiveLamp /></span>
          <UsageBadge />
          <span className="hidden md:inline text-sm truncate orbit-muted">{user?.email}</span>
          <OrbitButton variant="quiet" size="small" className="!h-9 !text-[11px]" onClick={logout} testid="orbit-logout">Sign out</OrbitButton>
        </div>
      </div>
    </header>
  );
}

const STEPS = [
  { n: 1, title: 'Install OrbitPrint HUB', body: 'On the computer your printers are plugged into.', download: true },
  { n: 2, title: 'Sign in there with this account', body: 'Name that computer — Office, Home, Store.' },
  { n: 3, title: 'It appears here', body: 'With every printer you choose to share, and its live status.' },
];

/** A Windows download button: asks the server for the current installer link; honest when it isn't published yet. */
function DownloadButton({ path, label, what, testid, variant = 'primary', size = 'small' }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const r = await orbitApi.get(path);
      window.open(r.data.url, '_blank', 'noopener');
      toast.success(`Downloading ${what} ${r.data.version || ''}`.trim());
    } catch (err) {
      toast.error(err?.response?.status === 404 ? `The ${what} installer isn't published yet — check back soon.` : errText(err));
    } finally { setBusy(false); }
  };
  return <OrbitButton size={size} variant={variant} busy={busy} onClick={go} testid={testid}>{label}</OrbitButton>;
}

export function HubDownloadButton(props) {
  return <DownloadButton path="/hub/download" label="Download the HUB for Windows" what="OrbitPrint HUB" testid="orbit-hub-download" {...props} />;
}

/** The Sender: the "OrbitPrint" printer for the computer you print FROM. */
export function SenderDownloadButton(props) {
  return <DownloadButton path="/hub/sender-download" label="Download OrbitPrint for Windows" what="OrbitPrint for Windows" testid="orbit-sender-download" variant="secondary" {...props} />;
}

function HowItAppears() {
  return (
    <>
      <h2 className="orbit-display" style={{ fontSize: 26 }}>How a location appears</h2>
      <ol className="mt-5 grid gap-5 sm:grid-cols-3 list-none p-0 m-0">
        {STEPS.map((s) => (
          <li key={s.n} className="flex gap-3">
            <span className="orbit-mono text-sm font-semibold flex-none h-7 w-7 rounded-full grid place-items-center" style={{ background: 'var(--ob-canvas-2)', border: '1px solid var(--ob-rule-2)' }}>{s.n}</span>
            <div>
              <p className="font-semibold leading-snug m-0">{s.title}</p>
              <p className="mt-1 text-sm leading-relaxed orbit-ink2 m-0">{s.body}</p>
            </div>
          </li>
        ))}
      </ol>
      <div className="mt-7 flex flex-wrap items-center gap-3">
        <HubDownloadButton />
        <span className="text-sm orbit-muted">Windows 10/11. Installs for your user only, no admin rights needed. Reinstalling keeps the pairing.</span>
      </div>
    </>
  );
}

/** The other half: the computer you print FROM gets an "OrbitPrint" printer in every Print dialog. */
function PrintFromThisComputer() {
  return (
    <section className="orbit-card mt-8 p-5 sm:p-6 flex flex-wrap items-center justify-between gap-4" data-testid="orbit-sender-card">
      <div className="min-w-0">
        <p className="orbit-label m-0">Print from this computer</p>
        <p className="mt-1 text-sm leading-relaxed orbit-ink2 m-0 max-w-xl">
          Install OrbitPrint for Windows and an <span className="font-semibold" style={{ color: 'var(--ob-ink)' }}>OrbitPrint</span> printer appears in every app's Print dialog.
          Print to it, pick a location and a printer, done. It also prints to the printers on that PC.
        </p>
      </div>
      <SenderDownloadButton />
    </section>
  );
}

function PairWithCode({ onPaired }) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await orbitApi.post('/locations/pair', { code, location_name: name });
      toast.success(r.data.message || 'Paired');
      setCode(''); setName('');
      onPaired();
    } catch (err) { toast.error(errText(err)); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="mt-6 pt-6" style={{ borderTop: '1px solid var(--ob-rule)' }} data-testid="orbit-pair-form">
      <p className="font-semibold m-0">Or pair with a code</p>
      <p className="mt-1 text-sm orbit-ink2">If the HUB shows a 6-character code instead of a sign-in, type it here and name the location.</p>
      <div className="mt-4 grid gap-3 sm:grid-cols-[160px_1fr_auto] sm:items-end">
        <OrbitInput label="Code" name="code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="ABC234" maxLength={6} className="orbit-mono tracking-[0.2em] uppercase" testid="orbit-pair-code" required />
        <OrbitInput label="Location name" name="location_name" value={name} onChange={(e) => setName(e.target.value)}
          placeholder="Office" maxLength={60} testid="orbit-pair-name" required />
        <div className="mb-4"><OrbitButton type="submit" size="small" busy={busy} testid="orbit-pair-submit">Pair</OrbitButton></div>
      </div>
    </form>
  );
}

export default function OrbitDashboardPage() {
  const { user, org } = useOrbitAuth();
  const [locations, setLocations] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [jobsKey, setJobsKey] = useState(0);
  const timer = useRef(null);
  const firstName = (user?.full_name || '').trim().split(' ')[0];
  const canManage = org?.role === 'owner' || org?.role === 'admin';
  const pollMs = usePollMs(POLL_LIVE_MS, POLL_FALLBACK_MS);

  const load = useCallback(async (quiet = false) => {
    try {
      const r = await orbitApi.get('/locations');
      setLocations(r.data || []);
    } catch (err) {
      if (!quiet) toast.error(errText(err, "Couldn't load your locations."));
      setLocations((prev) => prev || []);
    }
  }, []);

  useEffect(() => {
    load();
    const onVis = () => { if (document.visibilityState === 'visible') load(true); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [load, org?.id]);

  useEffect(() => {
    // the fallback poll: relaxed while the socket is live, 15 s when it is not
    timer.current = setInterval(() => load(true), pollMs);
    return () => clearInterval(timer.current);
  }, [load, pollMs]);

  // the server said a location or printer changed → re-fetch (no polling needed)
  useLiveRefresh('locations', () => load(true));

  const list = locations || [];
  const hasLocations = list.length > 0;
  const onlineCount = list.filter((l) => l.online).length;

  return (
    <div className="orbit-root min-h-screen">
      <TopBar />
      <main className="mx-auto max-w-6xl px-5 sm:px-8 py-9">
        <div className="flex flex-wrap items-end justify-between gap-5">
          <div>
            <p className="orbit-label orbit-boot orbit-d1 m-0">Locations</p>
            <h1 className="orbit-display orbit-boot orbit-d2 mt-1" style={{ fontSize: 'clamp(36px, 5vw, 56px)' }} data-testid="orbit-dashboard-title">
              {locations === null ? 'Loading…'
                : !hasLocations ? <>{firstName ? `Hi ${firstName}.` : 'Welcome.'} No locations yet.</>
                : <>
                    <span className="orbit-odo"><span className="orbit-mono" style={{ fontWeight: 500, fontStretch: '100%' }}>{list.length}</span></span>
                    {' '}{list.length === 1 ? 'location' : 'locations'} ·{' '}
                    <span className="orbit-odo"><span className="orbit-mono orbit-grad-text" style={{ fontWeight: 500, fontStretch: '100%' }}>{onlineCount}</span></span>
                    {' '}online
                  </>}
            </h1>
          </div>
          <div className="orbit-boot orbit-d3 flex items-start gap-3">
            {canManage && hasLocations && (
              <OrbitButton variant="secondary" size="small" onClick={() => setShowAdd((v) => !v)} testid="orbit-add-location">
                {showAdd ? 'Close' : 'Add a location'}
              </OrbitButton>
            )}
            <div title={hasLocations ? undefined : 'Needs a location first'}>
              <OrbitButton size="small" disabled={!hasLocations} onClick={() => setPrintOpen(true)} testid="orbit-print-file"><PrinterIcon /> Print a file</OrbitButton>
              {!hasLocations && <p className="mt-1.5 text-xs text-right orbit-muted m-0">Needs a location first.</p>}
            </div>
          </div>
        </div>

        <PlanBanner />

        {locations !== null && (!hasLocations || showAdd) && (
          <section className="orbit-card is-dashed orbit-boot orbit-d3 mt-8 p-6 sm:p-8" data-testid="orbit-empty-locations">
            <HowItAppears />
            {canManage && <PairWithCode onPaired={() => { setShowAdd(false); load(); }} />}
          </section>
        )}

        {hasLocations && (
          <div className="mt-8 grid gap-5 lg:grid-cols-2">
            {list.map((l, i) => <LocationCard key={l.id} location={l} delay={90 + i * 45} onChanged={() => load(true)} />)}
          </div>
        )}

        {hasLocations && <PrintFromThisComputer />}
        {hasLocations && <JobsPanel refreshKey={jobsKey} />}
      </main>
      <PrintFileDialog open={printOpen} onClose={() => setPrintOpen(false)} locations={list} onSent={() => setJobsKey((k) => k + 1)} />
    </div>
  );
}
